import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Header, LoadingError } from "@kaladel/react/Components";
import { classNames } from "@kaladel/react/Utilities";
import { FC, useCallback, useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router";
import { DisplayCurrency } from "../DisplayCurrency";
import { DisplayPercentage } from "../DisplayPercentage";
import styles from "./HomeCards.module.scss";
import { LoansOverviewGetResponse } from "ApiClient/models";
import { generateLoanChartData } from "Helpers/GenerateChartData";
import { useApp } from "../../AppContext";
import { useApiClient } from "Contexts/ApiClientContext";

export const LoansCard: FC = () => {
    const { api, apiCall } = useApiClient();
    const { activeCurrency } = useApp();

    const [loansOverview, setLoansOverview] = useState<LoansOverviewGetResponse | null>(null);

    const refreshLoansOverview = async () => {
        const response = await apiCall(() => api.loans.overview.get());

        if (response.kind === "success") {
            setLoansOverview(response.value);
        }
    };

    const getLoansData = useCallback(() => {
        if (!activeCurrency || !loansOverview) return null;

        const thisCurrency = loansOverview.balances.find(b => b.currencyId === activeCurrency);
        const hasAccount = !!thisCurrency;
        const totals = {
            balance: thisCurrency?.totalBalance ?? 0,
            originalLoanAmount: thisCurrency?.totalOriginalLoanAmount ?? 0,
            totalPayable: thisCurrency?.totalPayable ?? 0,
            paid: thisCurrency?.totalPaid ?? 0
        };

        return {
            hasAccount,
            totals,
            chartData: generateLoanChartData(activeCurrency, totals)
        };
    }, [activeCurrency, loansOverview]);

    useEffect(() => {
        refreshLoansOverview();
    }, []);

    if (!activeCurrency) return null;

    return (
        <Card>
            <Header title={<Link to="loans">Loans <FontAwesomeIcon icon={faChevronRight} /></Link>} level={1} />

            <LoadingError
                value={getLoansData()}
                loaded={data => (
                    <>
                        <div className={styles.breakdown}>

                            <div className={styles.line}>
                                <div className={styles.title}>
                                    Total Balance
                                </div>
                                <div className={styles.percentage}>
                                    <DisplayPercentage value={data.totals.balance / data.totals.totalPayable} minimumDP={1} maximumDP={1} />
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.balance} />
                                </div>
                            </div>

                            <div className={classNames([styles.line])}>
                                <div className={styles.title}>
                                    Total Paid
                                </div>
                                <div className={styles.percentage}>
                                    <DisplayPercentage value={data.totals.paid / data.totals.totalPayable} minimumDP={1} maximumDP={1} />
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.paid} />
                                </div>
                            </div>

                            <div className={classNames([styles.line])}>
                                <div className={styles.title}>
                                    Total Loan Amount
                                </div>
                                <div className={styles.percentage}>
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.originalLoanAmount} />
                                </div>
                            </div>

                            <div className={styles.line}>
                                <div className={styles.title}>
                                    Total Payable
                                </div>
                                <div className={styles.percentage}>
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.totalPayable} />
                                </div>
                            </div>

                            <div className={styles.line}>
                                <div className={styles.title}>
                                    Total Interest
                                </div>
                                <div className={styles.percentage}>
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.totalPayable - data.totals.originalLoanAmount} />
                                </div>
                            </div>

                            <Pie data={data.chartData} />
                        </div>
                    </>
                )}
            />
        </Card>

    );
};